import styled from 'styled-components';
import type { FunctionComponent } from 'react';
import { CrossMd, paletteHelper, spacingHelper, Text, mediaHelper } from '@property.com.au/ui-kit';
import { LightBoxContentWrapper } from './subcomponents/LightBoxContentWrapper/LightBoxContentWrapper';
import { useMediaGalleryLightBoxContext } from '../../contexts/MediaGalleryLightBoxContext/MediaGalleryLightBoxContext';
import { MediaCountButtons } from '../MediaCountButtons/MediaCountButtons';
import { mediaGalleryLightBoxId } from './MediaGalleryLightBox';
import {
  SearchByImageFeatureButton,
  SearchByImageFeatureButtonWrapper,
} from '../../../components/SearchByImageButton/SearchByImageButton';
import { MediaItemV2 } from '../../../app-kit/components/Image/types';

const LeftSideActionBar = styled.div`
  display: flex;
  align-items: center;
  /* added to avoid shifting timeline buttons, space matches width of media count if up to 999 items */
  flex: 0 0 ${spacingHelper('threeExtraLarge')};
`;
const RightSideActionBar = styled.div`
  display: flex;
  margin-right: -12px;
  align-items: center;
`;

const CurrentSlideText = styled(Text).attrs(() => ({ variant: 'body02' }))`
  color: ${paletteHelper('textInverted')};
`;

const ModalCloseButton = styled.button`
  color: ${paletteHelper('textInverted')};
  width: ${spacingHelper('twoExtraLarge')};
  margin-left: ${spacingHelper('small')};
  display: flex;
  justify-content: center;
  align-items: center;
  stroke-width: 1.2;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const SearchByImageWrapperDesktop = styled(SearchByImageFeatureButtonWrapper)`
  margin-right: ${spacingHelper('large')};

  ${mediaHelper('tabletMax')} {
    display: none;
  }
`;

const SearchByImageWrapperMobileTablet = styled(SearchByImageFeatureButtonWrapper)`
  margin-left: ${spacingHelper('medium')};

  ${mediaHelper('desktopMin')} {
    display: none;
  }
`;

interface MediaGalleryActionBarProps {
  opened: boolean;
  onClose: () => void;
  searchByImageButton: MediaItemV2['searchByImageButton'];
}

const StyledMediaGalleryActionBar = styled(LightBoxContentWrapper)`
  display: flex;
  justify-content: space-between;
  padding-top: calc(${spacingHelper('extraLarge')} + ${spacingHelper('twoExtraSmall')});
  height: 69px; /* to prevent layout shift when showing/hiding vibe button */

  ${mediaHelper('mobile')} {
    padding-top: calc(${spacingHelper('large')} + ${spacingHelper('twoExtraSmall')});
    padding-bottom: calc(${spacingHelper('large')} + ${spacingHelper('twoExtraSmall')});
  }
  ${mediaHelper('tabletMax')} {
    padding-left: ${spacingHelper('large')};
    padding-right: ${spacingHelper('large')};
  }
`;
export const MediaGalleryActionBar: FunctionComponent<MediaGalleryActionBarProps> = ({
  onClose,
  opened,
  searchByImageButton,
}) => {
  const { totalSlidesCount, currentSlideIndex, mediaCounts, setCurrentSlideByIndex } =
    useMediaGalleryLightBoxContext();

  const onMediaCountClick = (imageIndexFrom: number) =>
    setCurrentSlideByIndex(imageIndexFrom, false);

  return (
    <StyledMediaGalleryActionBar>
      <LeftSideActionBar>
        <CurrentSlideText>{`${currentSlideIndex + 1}/${totalSlidesCount}`}</CurrentSlideText>
        {searchByImageButton && (
          <SearchByImageWrapperMobileTablet>
            <SearchByImageFeatureButton searchByImageButton={searchByImageButton} />
          </SearchByImageWrapperMobileTablet>
        )}
      </LeftSideActionBar>
      <RightSideActionBar>
        {searchByImageButton && (
          <SearchByImageWrapperDesktop>
            <SearchByImageFeatureButton searchByImageButton={searchByImageButton} />
          </SearchByImageWrapperDesktop>
        )}
        <MediaCountButtons counts={mediaCounts} onClickHandler={onMediaCountClick} />
        <ModalCloseButton
          onClick={onClose}
          aria-label="Close Lightbox"
          aria-expanded={opened}
          aria-controls={mediaGalleryLightBoxId}
        >
          <CrossMd />
        </ModalCloseButton>
      </RightSideActionBar>
    </StyledMediaGalleryActionBar>
  );
};
